import { useLayoutEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  message,
} from "antd";
import {
  createDataLock,
  getDataLock,
  getDataLockCostConfig,
  getDataLockOptions,
  updateDataLock,
} from "../../services/domains/data-lock";
import type {
  DataLockAction,
  DataLockCostConfigNode,
  DataLockFeature,
  DataLockOptionAction,
  DataLockOptions,
} from "../../services/domains/data-lock/types";
import { CostSheetScopeEditor } from "./CostSheetScopeEditor";
import {
  buildDataLockPayload,
  createEmptyDataLockForm,
  dataLockDetailToForm,
  normalizeDataLockActions,
  resetFeatureSpecificForm,
  validateDataLockForm,
} from "./formMappers";
import { OrderScopeEditor } from "./OrderScopeEditor";
import styles from "./DataLock.module.scss";
import type {
  DataLockFormValidationErrorKey,
  DataLockFormValidationErrors,
  DataLockFormValues,
} from "./types";

export type DataLockFormModalProps = {
  open: boolean;
  mode: "create" | "edit";
  dataLockId?: string | null;
  canSubmit: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type CostConfigStatus = "idle" | "loading" | "loaded" | "error";

type CostConfigState = {
  status: CostConfigStatus;
  nodes: DataLockCostConfigNode[];
  error: string | null;
};

type CostRequestState = {
  generation: number;
  attempt: number;
  status: CostConfigStatus;
};

type FeatureOption = {
  value: string;
  label: string;
};

const FEATURE_LABELS: Record<DataLockFeature, string> = {
  ORDER: "Đơn hàng",
  ORDER_COST_SHEET: "Bảng chi phí đơn hàng",
};

const VALIDATION_MESSAGES_VI: Record<
  DataLockFormValidationErrorKey,
  string
> = {
  name: "Vui lòng nhập tên khóa dữ liệu",
  feature: "Vui lòng chọn tính năng",
  from: "Vui lòng chọn ngày bắt đầu",
  to: "Vui lòng chọn ngày kết thúc",
  actions: "Vui lòng chọn ít nhất một hành động",
  dateRange: "Ngày bắt đầu phải trước hoặc bằng ngày kết thúc",
  attributeField: "Vui lòng chọn thuộc tính cho tất cả các dòng",
  duplicateAttributeField:
    "Không được chọn trùng thuộc tính trong cùng một phạm vi",
};

const emptyCostConfig = (): CostConfigState => ({
  status: "idle",
  nodes: [],
  error: null,
});

const isDataLockFeature = (value: unknown): value is DataLockFeature =>
  value === "ORDER" || value === "ORDER_COST_SHEET";

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message.trim() ? error.message : fallback;

const normalizeFeatureOptions = (
  features: DataLockOptions["features"],
): FeatureOption[] => {
  if (!Array.isArray(features)) return [];

  const seen = new Set<string>();
  return features.reduce<FeatureOption[]>((result, item) => {
    const code =
      typeof item === "string"
        ? item.trim()
        : typeof item?.code === "string"
          ? item.code.trim()
          : "";
    if (!code || seen.has(code)) return result;

    const suppliedName =
      typeof item === "object" &&
      item !== null &&
      typeof item.name === "string"
        ? item.name.trim()
        : "";
    const fallback = isDataLockFeature(code) ? FEATURE_LABELS[code] : code;

    seen.add(code);
    result.push({
      value: code,
      label: suppliedName || fallback,
    });
    return result;
  }, []);
};

const normalizeActionOptions = (
  actions: DataLockOptionAction[] | undefined,
): Array<{ value: DataLockAction; label: string }> => {
  if (!Array.isArray(actions)) return [];

  const seen = new Set<string>();
  return actions.reduce<Array<{ value: DataLockAction; label: string }>>(
    (result, action) => {
      const code =
        typeof action?.code === "string" ? action.code.trim() : "";
      if (!code || seen.has(code)) return result;

      const name =
        typeof action.name === "string" ? action.name.trim() : "";
      seen.add(code);
      result.push({
        value: code,
        label: name || code,
      });
      return result;
    },
    [],
  );
};

const hasFeatureSelections = (values: DataLockFormValues) =>
  values.actions.length > 0 ||
  values.restrictedAttributes.length > 0 ||
  values.whitelistedAttributes.length > 0 ||
  values.restrictedGroups.length > 0 ||
  values.restrictedLines.length > 0;

const localizeValidationErrors = (
  errors: DataLockFormValidationErrors,
): DataLockFormValidationErrors =>
  (Object.keys(VALIDATION_MESSAGES_VI) as DataLockFormValidationErrorKey[])
    .reduce<DataLockFormValidationErrors>((localized, key) => {
      if (errors[key]) {
        localized[key] = VALIDATION_MESSAGES_VI[key];
      }
      return localized;
    }, {});

export const DataLockFormModal = ({
  open,
  mode,
  dataLockId = null,
  canSubmit,
  onClose,
  onSaved,
}: DataLockFormModalProps) => {
  const [formValues, setFormValues] = useState<DataLockFormValues>(
    createEmptyDataLockForm,
  );
  const [options, setOptions] = useState<DataLockOptions | null>(null);
  const [initialIdentity, setInitialIdentity] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [initialError, setInitialError] = useState<string | null>(null);
  const [costConfig, setCostConfig] =
    useState<CostConfigState>(emptyCostConfig);
  const [validationErrors, setValidationErrors] =
    useState<DataLockFormValidationErrors>({});
  const [saving, setSaving] = useState(false);

  const generationRef = useRef(0);
  const initialAttemptRef = useRef(0);
  const featureChoiceRef = useRef(0);
  const submitLockRef = useRef(false);
  const canSubmitRef = useRef(canSubmit);
  const featureConfirmRef =
    useRef<ReturnType<typeof Modal.confirm> | null>(null);
  const costRequestRef = useRef<CostRequestState>({
    generation: 0,
    attempt: 0,
    status: "idle",
  });
  const openIdentity = open
    ? JSON.stringify([mode, dataLockId])
    : null;
  canSubmitRef.current = canSubmit;

  const destroyFeatureConfirm = () => {
    const current = featureConfirmRef.current;
    featureConfirmRef.current = null;
    current?.destroy();
  };

  const clearFeatureConfirm = (
    handle: ReturnType<typeof Modal.confirm>,
  ) => {
    if (featureConfirmRef.current === handle) {
      featureConfirmRef.current = null;
    }
  };

  const isCurrentRequest = (generation: number, attempt?: number) =>
    open &&
    generationRef.current === generation &&
    (attempt === undefined || initialAttemptRef.current === attempt);

  const loadCostConfig = (generation: number, force = false) => {
    if (!open || generationRef.current !== generation) return;

    const currentRequest = costRequestRef.current;
    if (
      !force &&
      currentRequest.generation === generation &&
      (currentRequest.status === "loading" ||
        currentRequest.status === "loaded")
    ) {
      return;
    }

    const attempt =
      currentRequest.generation === generation
        ? currentRequest.attempt + 1
        : 1;
    costRequestRef.current = {
      generation,
      attempt,
      status: "loading",
    };
    setCostConfig((current) => ({
      status: "loading",
      nodes:
        currentRequest.generation === generation ? current.nodes : [],
      error: null,
    }));

    void Promise.resolve()
      .then(() => getDataLockCostConfig())
      .then((nodes) => {
        if (
          generationRef.current !== generation ||
          costRequestRef.current.generation !== generation ||
          costRequestRef.current.attempt !== attempt
        ) {
          return;
        }

        costRequestRef.current.status = "loaded";
        setCostConfig({
          status: "loaded",
          nodes: Array.isArray(nodes) ? nodes : [],
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (
          generationRef.current !== generation ||
          costRequestRef.current.generation !== generation ||
          costRequestRef.current.attempt !== attempt
        ) {
          return;
        }

        costRequestRef.current.status = "error";
        setCostConfig((current) => ({
          status: "error",
          nodes: current.nodes,
          error: getErrorMessage(
            error,
            "Không thể tải cấu hình chi phí đơn hàng",
          ),
        }));
      });
  };

  const loadInitialData = (generation: number, identity: string) => {
    const attempt = initialAttemptRef.current + 1;
    initialAttemptRef.current = attempt;
    submitLockRef.current = false;
    setSaving(false);
    setInitialIdentity(identity);
    setInitialLoading(true);
    setInitialError(null);
    setOptions(null);
    setFormValues(createEmptyDataLockForm());
    setValidationErrors({});
    setCostConfig(emptyCostConfig());
    costRequestRef.current = {
      generation,
      attempt: 0,
      status: "idle",
    };

    const optionsRequest = getDataLockOptions();
    const detailRequest =
      mode === "edit" && dataLockId
        ? getDataLock(dataLockId)
        : Promise.resolve(null);

    void Promise.all([optionsRequest, detailRequest])
      .then(([nextOptions, detail]) => {
        if (!isCurrentRequest(generation, attempt)) return;

        if (mode === "edit" && !dataLockId) {
          setInitialLoading(false);
          setInitialError("Thiếu mã khóa dữ liệu để chỉnh sửa");
          return;
        }

        const nextForm =
          mode === "edit" && detail
            ? dataLockDetailToForm(detail)
            : createEmptyDataLockForm();
        setOptions(nextOptions);
        setFormValues(nextForm);
        setInitialLoading(false);

        if (nextForm.feature === "ORDER_COST_SHEET") {
          loadCostConfig(generation);
        }
      })
      .catch((error: unknown) => {
        if (!isCurrentRequest(generation, attempt)) return;

        setInitialLoading(false);
        setInitialError(
          getErrorMessage(error, "Không thể tải dữ liệu khóa dữ liệu"),
        );
      });
  };

  useLayoutEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    destroyFeatureConfirm();
    featureChoiceRef.current += 1;
    submitLockRef.current = false;

    if (openIdentity) {
      loadInitialData(generation, openIdentity);
    } else {
      setSaving(false);
      setInitialIdentity(null);
      setInitialLoading(false);
      setInitialError(null);
      setOptions(null);
      setFormValues(createEmptyDataLockForm());
      setValidationErrors({});
      setCostConfig(emptyCostConfig());
    }

    return () => {
      destroyFeatureConfirm();
      if (generationRef.current === generation) {
        generationRef.current += 1;
      }
      featureChoiceRef.current += 1;
      submitLockRef.current = false;
    };
    // The open identity intentionally owns all async work for this modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIdentity]);

  const clearValidationErrors = (
    ...keys: DataLockFormValidationErrorKey[]
  ) => {
    setValidationErrors((current) => {
      if (!keys.some((key) => current[key])) return current;

      const next = { ...current };
      keys.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const applyFeature = (
    nextFeature: DataLockFeature,
    generation: number,
    choice: number,
  ) => {
    if (
      !open ||
      saving ||
      submitLockRef.current ||
      generationRef.current !== generation ||
      featureChoiceRef.current !== choice
    ) {
      return;
    }

    setFormValues((current) =>
      resetFeatureSpecificForm(current, nextFeature),
    );
    setValidationErrors({});
    if (nextFeature === "ORDER_COST_SHEET") {
      loadCostConfig(generation);
    }
  };

  const handleFeatureChange = (value: string) => {
    if (saving || !isDataLockFeature(value) || value === formValues.feature) {
      return;
    }

    destroyFeatureConfirm();
    const generation = generationRef.current;
    const choice = featureChoiceRef.current + 1;
    featureChoiceRef.current = choice;

    if (!hasFeatureSelections(formValues)) {
      applyFeature(value, generation, choice);
      return;
    }

    let confirmHandle: ReturnType<typeof Modal.confirm>;
    confirmHandle = Modal.confirm({
      title: "Đổi tính năng khóa dữ liệu?",
      content:
        "Hành động và phạm vi đã chọn sẽ bị xóa khi đổi tính năng.",
      okText: "Đổi tính năng",
      cancelText: "Giữ nguyên",
      centered: true,
      onOk: () => {
        clearFeatureConfirm(confirmHandle);
        applyFeature(value, generation, choice);
      },
      onCancel: () => clearFeatureConfirm(confirmHandle),
    });
    featureConfirmRef.current = confirmHandle;
  };

  const handleClose = () => {
    if (!saving) {
      destroyFeatureConfirm();
      featureChoiceRef.current += 1;
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!canSubmitRef.current) return;
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    featureChoiceRef.current += 1;
    destroyFeatureConfirm();

    const errors = localizeValidationErrors(
      validateDataLockForm(formValues),
    );
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      submitLockRef.current = false;
      return;
    }

    if (
      formValues.feature === "ORDER_COST_SHEET" &&
      costConfig.status !== "loaded"
    ) {
      message.error(
        costConfig.error || "Vui lòng tải xong cấu hình chi phí trước khi lưu",
      );
      submitLockRef.current = false;
      return;
    }

    if (mode === "edit" && !dataLockId) {
      message.error("Thiếu mã khóa dữ liệu để cập nhật");
      submitLockRef.current = false;
      return;
    }

    const generation = generationRef.current;
    setSaving(true);

    try {
      const payload = buildDataLockPayload(formValues);
      if (mode === "create") {
        await createDataLock(payload);
      } else if (dataLockId) {
        await updateDataLock(dataLockId, payload);
      } else {
        throw new Error("Thiếu mã khóa dữ liệu để cập nhật");
      }

      if (generationRef.current !== generation) return;
      message.success(
        mode === "create"
          ? "Tạo khóa dữ liệu thành công"
          : "Cập nhật khóa dữ liệu thành công",
      );
      onSaved();
    } catch (error: unknown) {
      if (generationRef.current !== generation) return;
      message.error(
        getErrorMessage(error, "Không thể lưu khóa dữ liệu"),
      );
    } finally {
      if (generationRef.current === generation) {
        submitLockRef.current = false;
        setSaving(false);
      }
    }
  };

  const hasCurrentInitialState =
    openIdentity !== null && initialIdentity === openIdentity;
  const visibleInitialLoading =
    open && (!hasCurrentInitialState || initialLoading);
  const visibleInitialError = hasCurrentInitialState ? initialError : null;
  const visibleOptions = hasCurrentInitialState ? options : null;
  const featureOptions = normalizeFeatureOptions(
    visibleOptions?.features ?? [],
  );
  const actionOptions = normalizeActionOptions(
    formValues.feature
      ? visibleOptions?.actions[formValues.feature]
      : undefined,
  );
  const costConfigBlocksSubmit =
    formValues.feature === "ORDER_COST_SHEET" &&
    costConfig.status !== "loaded";

  const footer = (
    <Space className={styles.FormFooter}>
      <Button disabled={saving} onClick={handleClose}>
        Hủy
      </Button>
      <Button
        type="primary"
        loading={saving}
        disabled={
          !canSubmit ||
          visibleInitialLoading ||
          Boolean(visibleInitialError) ||
          costConfigBlocksSubmit
        }
        onClick={() => void handleSubmit()}
      >
        Lưu
      </Button>
    </Space>
  );

  return (
    <Modal
      className={styles.FormModal}
      open={open}
      centered
      width={1100}
      title={
        mode === "create"
          ? "Tạo khóa dữ liệu"
          : "Chỉnh sửa khóa dữ liệu"
      }
      footer={footer}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      onCancel={handleClose}
    >
      {visibleInitialLoading ? (
        <Spin spinning>
          <div style={{ minHeight: 240 }} />
        </Spin>
      ) : visibleInitialError ? (
        <Alert
          type="error"
          showIcon
          message="Không thể mở biểu mẫu khóa dữ liệu"
          description={visibleInitialError}
          action={
            <Button
              size="small"
              disabled={saving}
              onClick={() => {
                if (openIdentity) {
                  loadInitialData(generationRef.current, openIdentity);
                }
              }}
            >
              Thử lại
            </Button>
          }
        />
      ) : visibleOptions ? (
        <Space
          direction="vertical"
          size="large"
          className={styles.FormContent}
        >
          <div className={styles.FormGrid}>
            <div className={styles.FormField}>
              <label className={styles.FieldLabel} htmlFor="data-lock-name">
                Tên khóa dữ liệu
              </label>
              <Input
                className={styles.Control}
                id="data-lock-name"
                aria-label="Tên khóa dữ liệu"
                value={formValues.name}
                disabled={saving}
                placeholder="Nhập tên khóa dữ liệu"
                onChange={(event) => {
                  setFormValues((current) => ({
                    ...current,
                    name: event.target.value,
                  }));
                  clearValidationErrors("name");
                }}
              />
              {validationErrors.name ? (
                <div className={styles.ValidationError}>
                  {validationErrors.name}
                </div>
              ) : null}
            </div>

            <div className={styles.FormField}>
              <label
                className={styles.FieldLabel}
                htmlFor="data-lock-description"
              >
                Mô tả
              </label>
              <Input.TextArea
                className={styles.Control}
                id="data-lock-description"
                aria-label="Mô tả"
                value={formValues.description}
                disabled={saving}
                autoSize={{ minRows: 2, maxRows: 4 }}
                placeholder="Nhập mô tả (không bắt buộc)"
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className={styles.FormGrid}>
            <div className={styles.FormField}>
              <label
                className={styles.FieldLabel}
                htmlFor="data-lock-feature"
              >
                Tính năng
              </label>
              <Select
                className={styles.Control}
                id="data-lock-feature"
                aria-label="Tính năng"
                value={formValues.feature}
                options={featureOptions}
                disabled={saving}
                placeholder="Chọn tính năng"
                onChange={handleFeatureChange}
              />
              {validationErrors.feature ? (
                <div className={styles.ValidationError}>
                  {validationErrors.feature}
                </div>
              ) : null}
            </div>
            <div className={styles.FormField}>
              <div className={styles.FieldLabel}>Trạng thái</div>
              <Switch
                aria-label="Đang hoạt động"
                checked={formValues.active}
                disabled={saving}
                checkedChildren="Hoạt động"
                unCheckedChildren="Tạm dừng"
                onChange={(active) =>
                  setFormValues((current) => ({ ...current, active }))
                }
              />
            </div>
          </div>

          <div className={styles.FormGrid}>
            <div className={styles.FormField}>
              <label className={styles.FieldLabel} htmlFor="data-lock-from">
                Từ ngày
              </label>
              <DatePicker
                className={styles.Control}
                id="data-lock-from"
                aria-label="Từ ngày"
                value={formValues.from}
                disabled={saving}
                format="DD/MM/YYYY"
                onChange={(from) => {
                  setFormValues((current) => ({ ...current, from }));
                  clearValidationErrors("from", "dateRange");
                }}
              />
              {validationErrors.from ? (
                <div className={styles.ValidationError}>
                  {validationErrors.from}
                </div>
              ) : null}
            </div>
            <div className={styles.FormField}>
              <label className={styles.FieldLabel} htmlFor="data-lock-to">
                Đến ngày
              </label>
              <DatePicker
                className={styles.Control}
                id="data-lock-to"
                aria-label="Đến ngày"
                value={formValues.to}
                disabled={saving}
                format="DD/MM/YYYY"
                onChange={(to) => {
                  setFormValues((current) => ({ ...current, to }));
                  clearValidationErrors("to", "dateRange");
                }}
              />
              {validationErrors.to ? (
                <div className={styles.ValidationError}>
                  {validationErrors.to}
                </div>
              ) : null}
            </div>
          </div>
          {validationErrors.dateRange ? (
            <div className={styles.ValidationError}>
              {validationErrors.dateRange}
            </div>
          ) : null}

          <div className={styles.FormField}>
            <div className={styles.FieldLabel}>Hành động</div>
            <Checkbox.Group
              aria-label="Hành động"
              value={formValues.actions}
              options={actionOptions}
              disabled={saving || !formValues.feature}
              onChange={(actions) => {
                setFormValues((current) => ({
                  ...current,
                  actions: normalizeDataLockActions(actions),
                }));
                clearValidationErrors("actions");
              }}
            />
            {validationErrors.actions ? (
              <div className={styles.ValidationError}>
                {validationErrors.actions}
              </div>
            ) : null}
          </div>

          {formValues.feature === "ORDER" ? (
            <div className={styles.FormSection}>
              <OrderScopeEditor
                fields={visibleOptions.order.fields}
                allOption={visibleOptions.order.wildcard}
                restrictedAttributes={formValues.restrictedAttributes}
                whitelistedAttributes={formValues.whitelistedAttributes}
                disabled={saving}
                onChange={(scope) => {
                  setFormValues((current) => ({ ...current, ...scope }));
                  clearValidationErrors(
                    "attributeField",
                    "duplicateAttributeField",
                  );
                }}
              />
              {validationErrors.attributeField ? (
                <div className={styles.ValidationError}>
                  {validationErrors.attributeField}
                </div>
              ) : null}
              {validationErrors.duplicateAttributeField ? (
                <div className={styles.ValidationError}>
                  {validationErrors.duplicateAttributeField}
                </div>
              ) : null}
            </div>
          ) : null}

          {formValues.feature === "ORDER_COST_SHEET" ? (
            <CostSheetScopeEditor
              groups={visibleOptions.orderCostSheet.groups}
              configNodes={costConfig.nodes}
              restrictedGroups={formValues.restrictedGroups}
              restrictedLines={formValues.restrictedLines}
              loading={costConfig.status === "loading"}
              error={costConfig.error}
              disabled={saving}
              onRetry={() =>
                loadCostConfig(generationRef.current, true)
              }
              onChange={(scope) =>
                setFormValues((current) => ({ ...current, ...scope }))
              }
            />
          ) : null}
        </Space>
      ) : null}
    </Modal>
  );
};

export default DataLockFormModal;
